import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAdmin = axios.create({ withCredentials: true });

export function useAdminData() {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [laneFilter, setLaneFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (laneFilter) params.lane = laneFilter;
      if (statusFilter) params.status = statusFilter;
      const [subRes, statsRes] = await Promise.all([
        axiosAdmin.get(`${API}/admin/submissions`, { params }),
        axiosAdmin.get(`${API}/admin/stats`),
      ]);
      setSubmissions(subRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) return "unauthorized";
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [laneFilter, statusFilter]);

  return { submissions, stats, loading, laneFilter, setLaneFilter, statusFilter, setStatusFilter, fetchSubmissions };
}

export function useAdminActions(fetchSubmissions) {
  const [noteText, setNoteText] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const handleStatusChange = useCallback(async (id, status) => {
    try {
      await axiosAdmin.patch(`${API}/admin/submissions/${id}/status`, null, { params: { status } });
      toast.success("Status updated");
      fetchSubmissions();
    } catch { toast.error("Failed to update"); }
  }, [fetchSubmissions]);

  const handleApprove = useCallback(async (id) => {
    try { await axiosAdmin.patch(`${API}/admin/submissions/${id}/approve`); toast.success("Approved"); fetchSubmissions(); } catch { toast.error("Failed"); }
  }, [fetchSubmissions]);

  const handleReject = useCallback(async (id) => {
    try { await axiosAdmin.patch(`${API}/admin/submissions/${id}/reject`); toast.success("Rejected"); fetchSubmissions(); } catch { toast.error("Failed"); }
  }, [fetchSubmissions]);

  const handleUpgrade = useCallback(async (id) => {
    try { await axiosAdmin.post(`${API}/admin/submissions/${id}/send-upgrade`); toast.success("Upgrade email sent"); fetchSubmissions(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }, [fetchSubmissions]);

  const handleConciergeFinal = useCallback(async (id) => {
    try { await axiosAdmin.post(`${API}/admin/submissions/${id}/send-concierge-final`); toast.success("Final invoice sent"); fetchSubmissions(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }, [fetchSubmissions]);

  const handleFollowUp = useCallback(async (id) => {
    try { await axiosAdmin.post(`${API}/admin/submissions/${id}/send-followup`); toast.success("Follow-up email sent"); fetchSubmissions(); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }, [fetchSubmissions]);

  const handleNoteSubmit = useCallback(async (id) => {
    if (!noteText.trim()) return;
    setNoteSubmitting(true);
    try { await axiosAdmin.post(`${API}/admin/submissions/${id}/notes`, { text: noteText }); toast.success("Note added"); setNoteText(""); fetchSubmissions(); }
    catch { toast.error("Failed to add note"); }
    finally { setNoteSubmitting(false); }
  }, [noteText, fetchSubmissions]);

  const handleToggleRow = useCallback((id) => { setExpandedRow((prev) => (prev === id ? null : id)); }, []);

  return { expandedRow, handleToggleRow, handleStatusChange, handleApprove, handleReject, handleUpgrade, handleConciergeFinal, handleFollowUp, noteText, setNoteText, handleNoteSubmit, noteSubmitting };
}

export function useTabData() {
  const [leads, setLeads] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [routeAnalyses, setRouteAnalyses] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [embedCode, setEmbedCode] = useState("");
  const [analyticsPeriod, setAnalyticsPeriod] = useState("week");
  const [marketingIntel, setMarketingIntel] = useState([]);
  const [marketingScans, setMarketingScans] = useState([]);
  const [digestPreview, setDigestPreview] = useState(null);
  const [emailQueue, setEmailQueue] = useState([]);
  const [orders, setOrders] = useState([]);
  const [soldOutItems, setSoldOutItems] = useState([]);

  const fetchTabData = useCallback(async (activeTab) => {
    try {
      if (activeTab === "leads") {
        const res = await axios.get(`${API}/leads`);
        setLeads(res.data);
      } else if (activeTab === "chats") {
        const res = await axios.get(`${API}/chat/sessions`);
        setChatSessions(res.data);
      } else if (activeTab === "routes") {
        const res = await axios.get(`${API}/routes/analyses`);
        setRouteAnalyses(res.data);
      } else if (activeTab === "analytics") {
        const [overview, daily] = await Promise.all([
          axios.get(`${API}/analytics/overview`, { params: { period: analyticsPeriod } }),
          axios.get(`${API}/analytics/daily`),
        ]);
        setAnalyticsData(overview.data);
        setDailyStats(daily.data);
      } else if (activeTab === "widget") {
        const res = await axios.get(`${API}/widget/embed-code`);
        setEmbedCode(res.data.embed_code);
      } else if (activeTab === "marketing") {
        const [intelRes, scansRes, digestRes] = await Promise.all([
          axios.get(`${API}/marketing/intel?limit=30`),
          axios.get(`${API}/marketing/scans?limit=5`),
          axios.get(`${API}/marketing/digest/preview`),
        ]);
        setMarketingIntel(intelRes.data);
        setMarketingScans(scansRes.data);
        setDigestPreview(digestRes.data);
      } else if (activeTab === "email_queue") {
        const res = await axiosAdmin.get(`${API}/admin/email-queue`);
        setEmailQueue(res.data);
      } else if (activeTab === "orders") {
        const res = await axiosAdmin.get(`${API}/admin/orders`);
        setOrders(res.data);
      } else if (activeTab === "sold_out") {
        const res = await axiosAdmin.get(`${API}/admin/sold-out`);
        setSoldOutItems(res.data);
      }
    } catch {
      toast.error("Failed to load tab data");
    }
  }, [analyticsPeriod]);

  return {
    leads, chatSessions, routeAnalyses, analyticsData, dailyStats,
    embedCode, analyticsPeriod, setAnalyticsPeriod,
    marketingIntel, marketingScans, digestPreview, emailQueue, orders, soldOutItems, fetchTabData,
  };
}
